import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Check, KeyRound, ShieldCheck } from 'lucide-react';
import { AddPasskeyForm, AddTotpForm } from '../../mfa/FactorForms';
import { NO_PASSKEY_SUPPORT, PASSKEY_HINT, BACKUP_PROMPT_LABEL } from '../../mfa/copy';
import { UserText } from '../../components/UserText';
import type { EnrollmentStepUp } from '../../api/mfa';
import type { InfoRequestForClient, WithdrawnInfoRequestForClient } from '@steward-app/steward-types';
import { WithdrawnRequests } from './WithdrawnRequests';
import { SIGN_OUT_PRIVACY_LINE } from '../../intake/frontCopy';
import { TermsAcceptanceLine } from '../../terms/TermsAcceptanceLine';

/**
 * The screens a client invited straight to onboarding sees after signing in
 * (crossmarc/steward-app#1764, the "Client: create login and passkey" mock board, steps 2 and 3).
 * Step 1 on the board ("Create your login") is Cognito's hosted sign-up, not a screen of ours:
 * an accepted deviation. No screen carries a "Step N of 3" label (#1884): a client who already has
 * a login skips the enrollment screens, so one label without the others read as a broken count.
 *
 * Every screen here is PRESENTATIONAL: its data arrives as props (the firm's name, the client's
 * first name, the access token the factor forms need) and its outcomes leave as callbacks.
 * `OnboardingClientGate.tsx` owns the reads and the routing, so a test or a screenshot harness
 * can mount any of these alone.
 *
 * Nothing here offers a way to create a workspace, pick one, or reach any other part of the
 * app. `OnboardingClientGate.test.tsx` pins that.
 */

const ICON_STROKE = 1.75;

export const cls = {
  page:    'flex min-h-screen flex-col items-center bg-cream px-4 pt-[8vh] pb-12',
  card:    'w-full max-w-[420px] bg-surface border border-line shadow-panel rounded-token px-6 py-8 sm:px-8 flex flex-col gap-4',
  /** Opt-in wide card for the requests page only (steward-app#2472): the 720px of the intake card column. */
  cardWide: 'w-full max-w-[720px] bg-surface border border-line shadow-panel rounded-token px-6 py-8 sm:px-8 flex flex-col gap-4',
  h2:      'm-0 font-serif text-[20px] font-normal leading-tight text-navy',
  body:    'm-0 text-[13px] leading-relaxed text-mid font-sans',
  small:   'text-[12px] leading-relaxed text-slate font-sans',
  primary: 'w-full px-5 py-3 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans',
  quiet:   'w-full px-4 py-2 text-[11px] font-semibold cursor-pointer bg-transparent border-none text-slate font-sans hover:text-navy',
};

/**
 * The firm's name as the card's main line, "with Steward" small beneath it (#1907), the same order
 * as the onboarding form's header (`FirmWordmark` + suffix, #1900). `FirmWordmark` itself is not
 * reused: it is sized for a one-line header bar and truncates with an ellipsis, which is right
 * beside a save dot and wrong for a centred card, where a long name should wrap (`break-words`
 * wraps an unbroken word too, `text-balance` evens the lines). No firm on record: the lockup is
 * "Steward" alone, as in the form header. Used by every screen in this file (enrollment, welcome).
 */
export function OnboardingBrandHeader({ firmName }: { firmName: string | null }) {
  return (
    <div className="text-center" data-testid="onboarding-brand">
      <div
        className="mx-auto max-w-full break-words text-balance font-serif text-[20px] font-semibold uppercase leading-snug tracking-[0.14em] text-navy"
        data-testid="onboarding-brand-name"
      >
        {firmName ? <UserText>{firmName}</UserText> : 'Steward'}
      </div>
      {firmName && (
        <div className="mt-1 text-[12px] text-slate font-sans" data-testid="onboarding-brand-suffix">with Steward</div>
      )}
      <div className="mx-auto mt-2 h-px w-8 bg-bronze" />
    </div>
  );
}

/**
 * Whether these screens sit inside the signed-in app (#2465) rather than taking the whole page
 * over. Inside the app the corner control is "Back to Steward" (a link to `/`), not "Sign out":
 * the person already has a workspace and a way to sign out, and leaving the form is what they want.
 */
export const OnboardingBackToAppContext = createContext(false);

const LEAVE_CLASS = 'bg-transparent border-none text-[12px] text-slate font-sans underline cursor-pointer max-sm:min-h-11 max-sm:min-w-11';

/** The one slot every onboarding screen uses for its corner control: "Sign out" for the takeover
 *  (when it is given a handler), "Back to Steward" inside the app. Nothing otherwise. */
export function LeaveControl({ onSignOut, className = '' }: { onSignOut?: () => void; className?: string }) {
  const backToApp = useContext(OnboardingBackToAppContext);
  if (backToApp) return <Link to="/" className={`${LEAVE_CLASS} ${className}`}>Back to Steward</Link>;
  if (!onSignOut) return null;
  return <button type="button" onClick={onSignOut} className={`${LEAVE_CLASS} ${className}`}>Sign out</button>;
}

/**
 * `wide` (steward-app#2472) is opt-in and used only by the requests page; every other screen,
 * `intake/IntakeSecondCheck.tsx` included, keeps the 420px column. `hideLeave` drops the corner
 * control on a screen that carries its own primary "Sign out" / "Back to Steward" (#2473).
 */
export function Shell({ children, onSignOut, wide = false, hideLeave = false }: { children: ReactNode; onSignOut?: () => void; wide?: boolean; hideLeave?: boolean }) {
  return (
    <div className={cls.page}>
      <div className={`w-full ${wide ? 'max-w-[720px]' : 'max-w-[420px]'} flex flex-col gap-3`}>
        {children}
      </div>
      {!hideLeave && <LeaveControl onSignOut={onSignOut} className="mt-6" />}
    </div>
  );
}

export { SIGN_OUT_PRIVACY_LINE };

/**
 * The primary action of a takeover thank-you screen (steward-app#2473): "Sign out" for the
 * takeover, "Back to Steward" (a link to `/`) inside the signed-in app, where signing out would
 * end the person's real session. Takes the place of the corner control on those screens.
 * Nothing when it is a takeover with no sign-out handler.
 */
export function ThankYouLeave({ onSignOut }: { onSignOut?: () => void }) {
  const backToApp = useContext(OnboardingBackToAppContext);
  if (backToApp) return <Link to="/" className={`${cls.primary} block text-center no-underline`}>Back to Steward</Link>;
  if (!onSignOut) return null;
  return <button type="button" className={cls.primary} onClick={onSignOut}>Sign out</button>;
}

/**
 * Step 2, "Protect your details": add a passkey (or, where the browser cannot, an authenticator
 * app), then the optional backup. `onEnrolled` fires once a first factor exists and the client
 * chooses to go on.
 *
 * Two optional props, both added for the snapshot form (crossmarc/steward-app#2158) and both
 * inert when left out, so the onboarding gate that already renders this is unchanged:
 *
 *  - `onFactorAdded` is called each time a factor is added, with which kind and with what the
 *    server said about the step-up it minted alongside (`EnrollmentStepUp`). This screen used
 *    to drop that answer; a host that must know the second check is done needs it.
 *  - `otherRoute`, when passed, offers the authenticator app on the FIRST screen of a browser
 *    that supports passkeys, under the passkey button, with a way back. Without it the
 *    authenticator app is reachable only after a passkey has been added (or when the browser
 *    has no passkey support), which leaves a person whose passkey prompt fails with no route.
 *    The two labels are the host's wording.
 */
export function OnboardingEnrollment({ token, firmName, onEnrolled, onSignOut, supportsPasskey = browserSupportsWebAuthn(), onFactorAdded, otherRoute }: {
  token: string;
  firmName: string | null;
  onEnrolled: () => void;
  onSignOut?: () => void;
  supportsPasskey?: boolean;
  onFactorAdded?: (kind: 'passkey' | 'authenticator', stepUp: EnrollmentStepUp) => void;
  otherRoute?: { useAuthenticatorLabel: string; usePasskeyLabel: string };
}) {
  const [step, setStep] = useState<'primary' | 'primary-totp' | 'backup' | 'adding-backup' | 'backup-done'>('primary');

  return (
    <Shell onSignOut={onSignOut}>
      <div className={cls.card} data-testid="onboarding-enrollment">
        <OnboardingBrandHeader firmName={firmName} />
        {step === 'primary' && (
          <>
            <div className="flex flex-col items-center text-center gap-2.5">
              <div className="flex h-12 w-12 items-center justify-center rounded-token bg-bronze/12 text-bronze">
                <ShieldCheck size={22} strokeWidth={ICON_STROKE} aria-hidden />
              </div>
              <h1 className={cls.h2}>Protect your details</h1>
              <p className={cls.body}>
                Add a second check at sign-in. It keeps your details safe, even if one credential is ever exposed.
              </p>
            </div>
            {supportsPasskey
              ? (
                <>
                  <AddPasskeyForm
                    token={token}
                    buttonLabel="Add your passkey"
                    hint={PASSKEY_HINT}
                    onDone={(stepUp) => { onFactorAdded?.('passkey', stepUp); setStep('backup'); }}
                  />
                  {otherRoute && (
                    <button type="button" className={cls.quiet} onClick={() => setStep('primary-totp')}>
                      {otherRoute.useAuthenticatorLabel}
                    </button>
                  )}
                </>
              )
              : (
                <div>
                  <p className={`${cls.small} text-center mb-4 mt-0`}>{NO_PASSKEY_SUPPORT}</p>
                  <AddTotpForm token={token} onDone={(stepUp) => { onFactorAdded?.('authenticator', stepUp); onEnrolled(); }} />
                </div>
              )}
          </>
        )}
        {step === 'primary-totp' && otherRoute && (
          <>
            <AddTotpForm token={token} onDone={(stepUp) => { onFactorAdded?.('authenticator', stepUp); onEnrolled(); }} />
            <button type="button" className={cls.quiet} onClick={() => setStep('primary')}>
              {otherRoute.usePasskeyLabel}
            </button>
          </>
        )}
        {step === 'backup' && (
          <>
            <div className="flex items-center gap-2.5 rounded-token border border-line px-3 py-2.5">
              <Check size={16} strokeWidth={ICON_STROKE} aria-hidden className="text-bronze-text" />
              <span className="text-[13px] text-navy font-sans">Your passkey is set up.</span>
            </div>
            <strong className="text-[13px] text-navy font-sans">{BACKUP_PROMPT_LABEL}</strong>
            <p className={`${cls.small} m-0`}>
              {/* Not `BACKUP_PROMPT_BODY`: that one ends "later from Settings", and this client has no
                  Settings page. The mock's "you can always add it later" is dropped for the same
                  reason: nothing in this client's app offers a later (coordinator, #1764 round 2). */}
              An authenticator app code works even if you lose the device your passkey lives on. It takes a minute.
            </p>
            <button type="button" className={`${cls.primary} flex items-center justify-center gap-2`} onClick={() => setStep('adding-backup')}>
              <KeyRound size={15} strokeWidth={ICON_STROKE} aria-hidden />
              Add an authenticator app
            </button>
            <button type="button" className={cls.quiet} onClick={onEnrolled}>Continue to your details</button>
          </>
        )}
        {step === 'adding-backup' && (
          <AddTotpForm token={token} onDone={(stepUp) => { onFactorAdded?.('authenticator', stepUp); setStep('backup-done'); }} />
        )}
        {step === 'backup-done' && (
          <>
            <p className="m-0 text-center text-[13px] text-navy font-sans">Authenticator app added.</p>
            <button type="button" className={cls.primary} onClick={onEnrolled}>Continue to your details</button>
          </>
        )}
      </div>
    </Shell>
  );
}

/**
 * Step 3, the welcome page at `/onboarding`. The board's "Welcome, Wei" and "Dana Lee asked"
 * become the profile's first name (or plain "Welcome") and the firm's name: the client's own
 * name is what they told Steward, and no advisor's name is carried to this page.
 *
 * Start, with {@link TermsAcceptanceLine} under it, is where an onboarding client accepts the
 * Terms (steward-app#2630): `onStart` records it before the form opens. A rejection keeps the
 * client here with a fixed line, never the server's own text.
 */
export function OnboardingWelcome({ firmName, givenName, onStart, onSignOut }: {
  firmName: string | null;
  givenName?: string;
  onStart: () => void | Promise<void>;
  onSignOut?: () => void;
}) {
  const name = givenName?.trim();
  const firm = firmName ? <UserText>{firmName}</UserText> : 'Your advisor';
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function start() {
    setBusy(true); setFailed(false);
    try {
      await onStart();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }
  return (
    <Shell onSignOut={onSignOut}>
      <div className={cls.card} data-testid="onboarding-welcome">
        <OnboardingBrandHeader firmName={firmName} />
        <h1 className={cls.h2}>{name ? <>Welcome, <UserText>{name}</UserText></> : 'Welcome'}</h1>
        <p className={cls.body}>{firm} has asked you to fill in a few details so they can open your accounts.</p>
        <p className={cls.body}>It takes about 15 minutes. Your answers save as you go.</p>
        <p className={`m-0 ${cls.small}`}>You can stop and come back anytime.</p>
        {failed && <p className={cls.body} role="alert">We could not record your agreement just now. Please try again in a moment.</p>}
        <button type="button" className={cls.primary} onClick={() => void start()} disabled={busy}>Start</button>
        <TermsAcceptanceLine className="m-0 text-center" />
      </div>
    </Shell>
  );
}

/**
 * The page after the client sends the form, and where a client who has already sent lands when
 * they return (#1976). `onReview` opens the ordinary form again. Focus moves to the heading on
 * mount, as `IntakeConfirmation` does, so a screen reader announces the outcome of the send.
 */
export function OnboardingCompleted({ firmName, onReview, onSignOut, openRequests = [], addedRequests = [], withdrawnRequests = [], onOpenRequests }: {
  firmName: string | null;
  onReview: () => void;
  onSignOut?: () => void;
  /** When the form was sent. Not shown since #2433 (the requests page replaced the lists that used it); kept so callers need no change. */
  sentAt?: string | null;
  /** The advisor's requests that still have nothing from the client, then those that do. Both
   *  come from the form read and the client's own saved answers (`splitRequests`). */
  openRequests?: readonly InfoRequestForClient[];
  addedRequests?: readonly InfoRequestForClient[];
  /** Requests the firm withdrew after the client had added to them (steward-app#2302). */
  withdrawnRequests?: readonly WithdrawnInfoRequestForClient[];
  /** Opens the requests page (steward-app#2433). Without it the "Add them" button is left out. */
  onOpenRequests?: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, []);
  const firm = firmName ? <UserText>{firmName}</UserText> : 'your advisor';
  const firmCap = firmName ? <UserText>{firmName}</UserText> : 'Your advisor';
  const backToApp = useContext(OnboardingBackToAppContext);
  // Nothing outstanding (steward-app#2473): the thank-you takes the sign-out treatment. With open
  // requests the screen keeps its "Add them" layout (the send now lands on the requests page,
  // #2471, but this one is still reachable by URL).
  const nothingOutstanding = openRequests.length === 0;
  return (
    <Shell onSignOut={onSignOut} hideLeave={nothingOutstanding}>
      <div className={cls.card} data-testid="onboarding-completed">
        <OnboardingBrandHeader firmName={firmName} />
        <h1 ref={headingRef} tabIndex={-1} className={`${cls.h2} outline-none`}>Thank you. Your answers are with {firm}.</h1>
        <p className={cls.body}>
          {nothingOutstanding ? '' : 'You can close this page now. '}If something changes, come back with the same email address and update your answers.
        </p>
        {nothingOutstanding && !backToApp && <p className={cls.body} data-testid="completed-sign-out-line">{SIGN_OUT_PRIVACY_LINE}</p>}
        {openRequests.length + addedRequests.length > 0 && (
          <section aria-labelledby="completed-requests-heading" data-testid="onboarding-completed-requests" className="flex flex-col gap-2">
            <h2 id="completed-requests-heading" className="sr-only">Requests from {firm}</h2>
            {openRequests.length > 0 ? (
              <>
                <p className={cls.body} data-testid="completed-requests-open">{firmCap} has asked for {openRequests.length === 1 ? 'one' : openRequests.length} more {openRequests.length === 1 ? 'thing' : 'things'}.</p>
                {onOpenRequests && <button type="button" className={cls.primary} onClick={onOpenRequests}>Add them</button>}
              </>
            ) : (
              <p className={cls.body} data-testid="completed-requests-added">You have added everything {firm} requested.</p>
            )}
          </section>
        )}
        <WithdrawnRequests firmCap={firmCap} withdrawn={withdrawnRequests} testId="completed-requests-withdrawn" />
        {nothingOutstanding && <ThankYouLeave onSignOut={onSignOut} />}
        <button type="button" onClick={onReview} className="self-start bg-transparent border-none p-0 text-[13px] text-navy font-sans underline cursor-pointer">
          Review my answers
        </button>
      </div>
    </Shell>
  );
}

/** A read the gate needs failed. */
export function OnboardingLoadError({ onRetry, onSignOut }: { onRetry: () => void; onSignOut?: () => void }) {
  return (
    <Shell onSignOut={onSignOut}>
      <div className={cls.card} role="alert">
        <p className={cls.body}>We could not load your sign-in settings. Please try again.</p>
        <button type="button" className={cls.primary} onClick={onRetry}>Try again</button>
      </div>
    </Shell>
  );
}
